/**
 * Loads data for a screen.
 *
 * The three states are returned separately rather than collapsed into `data | null`, because a
 * screen that cannot distinguish "still loading" from "loaded and empty" shows "no findings" to
 * someone whose request is still in flight — which reads as good news and is not.
 */

import { useEffect, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, error: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ data: null, error: null, loading: true });

    void fn()
      .then((data) => {
        // Guarded because a screen the user has already navigated away from must not write state.
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });

    return (): void => {
      cancelled = true;
    };
    // `deps` is the caller's dependency list, passed through deliberately so a screen can refetch
    // when its inputs change.
  }, deps);

  return state;
}
